'use client';

import { FormEvent, useRef, useState } from 'react';

import type HCaptcha from '@hcaptcha/react-hcaptcha';
import ExclamationMark from 'images/icons/exclamation-mark.svg';
import { css, cx } from 'styled-system/css';
import { button } from 'styled-system/recipes';

import { CheckCircleIcon, PaperPlaneTiltIcon } from '@/components/atoms/PhosphorIcons/PhosphorIcons';
import { LazyHCaptcha } from '@/components/functional/LazyHCaptcha';
import { Link as NextLink } from '@/rsc/ui/Link.client';
// Direct path, not the @/rsc/ui barrel: the barrel drags ParsedHTMLText (+ its CSS) into the island.
import { Text } from '@/rsc/ui/Text';
import { IS_CAPTCHA_ENABLED } from '@/utils/captcha';

import { ToastRegion, showToast } from './Toast.client';

// Same hand-rolled validation as common/Newsletter/NewsletterForm (messages and rules mirror the
// former zod schema).
const EMAIL_REQUIRED_MESSAGE = 'Please complete this required field.';
const EMAIL_FORMAT_MESSAGE = 'Email must be formatted correctly.';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUBMIT_ERROR_MESSAGE = "Couldn't submit form 😔";
const UNEXPECTED_ERROR_MESSAGE = 'Unexpected error occurred';

type NewsletterFormData = { email: string; agreement: boolean };
type FormErrors = { email?: string; agreement?: boolean };

const validate = (values: NewsletterFormData): FormErrors => {
  const errors: FormErrors = {};
  const email = values.email.trim();
  if (!email) errors.email = EMAIL_REQUIRED_MESSAGE;
  else if (!EMAIL_PATTERN.test(email)) errors.email = EMAIL_FORMAT_MESSAGE;
  if (!values.agreement) errors.agreement = true;
  return errors;
};

// The generated mutation (and the GraphQL client) load on the first submit, not with the page:
// the generated module carries every document plus react-query.
const subscribeToNewsletter = async (email: string, captchaToken: string) => {
  const { useAddSubscriberToNewsletterMutation } = await import('@/graphql/generated');
  return useAddSubscriberToNewsletterMutation.fetcher({ email, captchaToken })();
};

const iconTileClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bg: 'rgba(217,252,89,0.15)',
  border: '1px solid rgba(217,252,89,0.25)',
});

// Chakra `Input variant="unstyled"` + the form's sx (unstyled drops the size's height/padding).
const inputClass = css({
  width: '100%',
  minWidth: 0,
  outline: 0,
  position: 'relative',
  appearance: 'none',
  fontSize: { base: 'md', sm: 'lg' },
  height: 'auto',
  border: '1px solid',
  borderColor: 'rgba(255,255,255,0.12)',
  borderRadius: '0',
  color: 'text.500',
  backgroundColor: 'rgba(255,255,255,0.05)',
  paddingX: '16px',
  paddingY: '10px',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  _placeholder: { color: 'whiteAlpha.400' },
  _hover: { borderColor: 'rgba(255,255,255,0.25)' },
  _focus: { borderColor: 'rgba(217,252,89,0.6)', boxShadow: '0 0 0 1px rgba(217,252,89,0.4)' },
  '&[aria-invalid=true]': { borderColor: 'red.300', boxShadow: 'none' },
  _readOnly: { boxShadow: 'none', userSelect: 'all' },
});

// Chakra Checkbox size lg (root label carries the borderColor the control inherits).
const checkboxRootClass = css({
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'top',
  position: 'relative',
  bg: 'transparent',
  borderRadius: 'base',
  borderColor: 'whiteAlpha.400',
});

const checkboxControlClass = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  verticalAlign: 'top',
  userSelect: 'none',
  flexShrink: 0,
  w: 5,
  h: 5,
  borderWidth: '2px',
  borderStyle: 'solid',
  borderRadius: 'sm',
  borderColor: 'inherit',
  color: 'white',
  transitionProperty: 'box-shadow',
  transitionDuration: 'normal',
  '&[data-checked]': { bg: 'blue.500', borderColor: 'blue.500', color: 'white' },
  '&[data-invalid]': { borderColor: 'red.500' },
  '[data-focus-visible] &, input:focus-visible + &': { boxShadow: 'outline' },
});

const visuallyHiddenClass = css({
  border: '0',
  clip: 'rect(0, 0, 0, 0)',
  height: '1px',
  width: '1px',
  margin: '-1px',
  padding: '0',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  position: 'absolute',
});

// Design-system primary button: md (40px) on phones, lg (48px) from sm — the input's height.
// The recipe's `_disabled` covers the loading state (the button is disabled while submitting).
const submitClass = cx(
  button({ variant: 'primary', tone: 'dark', size: { base: 'md', sm: 'lg' } }),
  css({ flexShrink: 0 }),
);

// Chakra Spinner inside ButtonSpinner.
const spinnerClass = css({
  display: 'inline-block',
  borderColor: 'currentColor',
  borderStyle: 'solid',
  borderRadius: '99999px',
  borderWidth: '2px',
  borderBottomColor: 'transparent',
  borderInlineStartColor: 'transparent',
  width: '1em',
  height: '1em',
  animation: 'sh-more-spin 0.45s linear infinite',
});

type Props = {
  infoText: string;
  subscribeText: string;
  marketingText: string;
  marketingLinkLabel: string;
  emailLabel: string;
  emailPlaceholder: string;
  successLabel: string;
  privacyPolicyHref: string;
};

/** common/Newsletter's form + success state as one Chakra-free island. */
export const NewsletterForm = ({
  infoText,
  subscribeText,
  marketingText,
  marketingLinkLabel,
  emailLabel,
  emailPlaceholder,
  successLabel,
  privacyPolicyHref,
}: Props) => {
  const captchaRef = useRef<HCaptcha>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [values, setValues] = useState<NewsletterFormData>({ email: '', agreement: false });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const resetCaptcha = () => {
    setCaptchaToken(null);
    captchaRef.current?.resetCaptcha();
  };

  // Newsletter's onSubmit: a failed mutation is toasted here and does not reach the form's catch.
  const subscribe = async (email: string, token: string) => {
    try {
      await subscribeToNewsletter(email, token);
      setIsSuccess(true);
    } catch (e) {
      showToast({ status: 'error', title: (e as unknown as Error).toString() || UNEXPECTED_ERROR_MESSAGE });
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    let token = captchaToken;
    if (IS_CAPTCHA_ENABLED && !token) {
      const captchaResult = await captchaRef.current?.execute({ async: true });
      if (captchaResult?.response) token = captchaResult.response;
    }

    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (nextErrors.email || nextErrors.agreement) return;

    setIsSubmitting(true);
    try {
      if (IS_CAPTCHA_ENABLED && !token) throw new Error('Captcha token is not available');
      await subscribe(values.email.trim(), token ?? '');
      setValues({ email: '', agreement: false });
      setErrors({});
    } catch {
      showToast({ status: 'error', title: SUBMIT_ERROR_MESSAGE });
    } finally {
      setIsSubmitting(false);
      resetCaptcha();
    }
  };

  if (isSuccess) {
    return (
      <>
        <div
          className={css({
            display: 'flex',
            mx: 'auto',
            gap: 8,
            alignItems: 'center',
            maxW: '694px',
            flexDirection: { base: 'column', sm: 'row' },
          })}
        >
          <div className={cx(iconTileClass, css({ w: '72px', h: '72px', flexShrink: 0, borderRadius: '16px' }))}>
            <CheckCircleIcon size={40} color="rgba(217,252,89,0.9)" />
          </div>
          <Text
            as="span"
            variant="component-heading-large/xl"
            className={css({ flex: '1', textAlign: { base: 'center', sm: 'start' }, color: 'text.500' })}
          >
            {successLabel}
          </Text>
        </div>
        <ToastRegion />
      </>
    );
  }

  const hasEmailError = !!errors.email;

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: { base: 'column', md: 'row' },
        gap: { base: 10, md: 16, lg: 24 },
        alignItems: { md: 'center' },
        w: '100%',
      })}
    >
      <div className={css({ display: 'flex', flexDirection: 'column', gap: 5, flex: '1', maxW: { md: '400px' } })}>
        <div className={cx(iconTileClass, css({ w: '48px', h: '48px', borderRadius: '12px' }))}>
          <PaperPlaneTiltIcon size={24} color="rgba(217,252,89,0.9)" />
        </div>
        <Text
          as="span"
          variant={{ base: 'component-heading-large/lg', sm: 'component-heading-large/xl' }}
          className={css({ color: 'text.500', textWrap: 'balance' })}
        >
          {infoText}
        </Text>
      </div>
      <form
        className={css({ display: 'flex', flexDirection: 'column', gap: 4, flex: '1', maxW: { md: '542px' } })}
        onSubmit={onSubmit}
        onFocusCapture={() => setHasInteracted(true)}
        noValidate
      >
        <div className={css({ display: 'grid', flex: '1' })}>
          <div
            className={css({
              display: 'flex',
              gap: 4,
              alignItems: 'flex-end',
              flexWrap: { base: 'wrap', sm: 'nowrap' },
            })}
          >
            {/* Chakra FormControl (isRequired) + FormLabel with its required indicator */}
            <div
              role="group"
              className={css({ width: '100%', position: 'relative', flex: '1', minW: { base: '100%', sm: 'auto' } })}
            >
              <label
                htmlFor="newsletter-email"
                className={css({
                  display: 'block',
                  textAlign: 'start',
                  fontSize: 'md',
                  fontWeight: 'medium',
                  marginEnd: 3,
                  mb: 2,
                  color: 'whiteAlpha.700',
                })}
              >
                {emailLabel}
                <span role="presentation" aria-hidden="true" className={css({ marginStart: 1, color: 'red.500' })}>
                  *
                </span>
              </label>
              <input
                id="newsletter-email"
                name="email"
                type="email"
                value={values.email}
                onChange={(event) => setValues((previous) => ({ ...previous, email: event.target.value }))}
                placeholder={emailPlaceholder}
                required
                aria-required="true"
                aria-invalid={hasEmailError || undefined}
                aria-describedby={hasEmailError ? 'newsletter-email-error' : undefined}
                readOnly={isSubmitting}
                className={inputClass}
              />
            </div>
            <button
              type="submit"
              className={submitClass}
              disabled={isSubmitting}
              data-loading={isSubmitting ? '' : undefined}
            >
              {isSubmitting ? (
                <span
                  className={css({
                    display: 'flex',
                    alignItems: 'center',
                    position: 'absolute',
                    fontSize: '1em',
                    lineHeight: 'normal',
                  })}
                >
                  <span className={spinnerClass}>
                    <span className={visuallyHiddenClass}>Loading...</span>
                  </span>
                </span>
              ) : null}
              <span style={isSubmitting ? { opacity: 0 } : undefined}>{subscribeText}</span>
            </button>
          </div>
          {hasEmailError && (
            <div id="newsletter-email-error" className={css({ display: 'flex', alignItems: 'center', mt: 2 })}>
              <ExclamationMark />
              <Text variant="text-sm/lineHeight-5/font-semibold" className={css({ ml: 2, color: 'red.300' })}>
                {errors.email}
              </Text>
            </div>
          )}
          <div className={css({ display: 'flex', alignItems: 'flex-start', gap: 3, mt: 6 })}>
            <label className={checkboxRootClass}>
              <input
                type="checkbox"
                name="agreement"
                checked={values.agreement}
                onChange={(event) =>
                  !isSubmitting && setValues((previous) => ({ ...previous, agreement: event.target.checked }))
                }
                required
                aria-invalid={errors.agreement || undefined}
                aria-readonly={isSubmitting || undefined}
                // The consent copy is a sibling, not the label's content, so without this the
                // checkbox has no accessible name
                aria-label={`${marketingText} ${marketingLinkLabel}`}
                className={visuallyHiddenClass}
              />
              <span
                aria-hidden="true"
                className={checkboxControlClass}
                data-checked={values.agreement ? '' : undefined}
                data-invalid={errors.agreement ? '' : undefined}
              >
                {values.agreement ? (
                  <svg
                    viewBox="0 0 12 10"
                    className={css({
                      width: '1.2em',
                      fontSize: '2xs',
                      animation: 'sh-more-check-in 0.2s ease-out',
                      fill: 'none',
                      strokeWidth: 2,
                      stroke: 'currentColor',
                      strokeDasharray: 16,
                    })}
                  >
                    <polyline points="1.5 6 4.5 9 10.5 1" />
                  </svg>
                ) : null}
              </span>
            </label>
            <Text
              as="span"
              variant="text-xs/lineHeight-4/font-normal/0.75rem"
              className={errors.agreement ? css({ color: 'red.300' }) : css({ color: 'whiteAlpha.600' })}
            >
              {marketingText}{' '}
              <NextLink
                href={privacyPolicyHref}
                prefetch={false}
                className={css({ fontWeight: 'semibold', textDecoration: 'underline' })}
              >
                {marketingLinkLabel}
              </NextLink>
            </Text>
          </div>
        </div>
        {IS_CAPTCHA_ENABLED && hasInteracted && (
          <div>
            <LazyHCaptcha
              onVerify={(token) => setCaptchaToken(token)}
              ref={captchaRef}
              size="invisible"
              sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY}
            />
          </div>
        )}
      </form>
      <ToastRegion />
    </div>
  );
};
